import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import aiAgentApi from '@/pages/AIAgent/ai-agent.api.ts'
import { IAIAgentMessage } from '@/pages/AIAgent/ai-agent.types.ts'
import useHttpLoader from '@/shared/hooks/useHttpLoader.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import { useRecoilValue } from 'recoil'
import userAtom from '@/pages/User/user.atom.ts'

const useAiAssistanceMessagingCtrl = (equipmentId: IEquipment['id']) => {
  const userId = useRecoilValue(userAtom).id

  const [messages, setMessages] = useState<IAIAgentMessage[]>([])
  const messagesEndRef = useRef<HTMLDivElement>(null)

  const { wait: waitMessages, loading: messagesLoading } = useHttpLoader()
  const { wait: waitAsk, loading: asking } = useHttpLoader()

  useEffect(() => {
    waitMessages(aiAgentApi.getDialogMessages(equipmentId), (resp) => {
      if (resp.status === 'success') {
        setMessages(resp.body)
      } else {
        setMessages([])
      }
    })

    return () => {
      aiAgentApi.getDialogMessages.abort()
    }
  }, [equipmentId])

  useLayoutEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  const handleAsk = (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || !equipmentId || asking || !userId) return

    const payload = {
      equipmentId,
      markdownText: trimmed,
    }

    waitAsk(aiAgentApi.askQuestion(payload), (resp) => {
      if (resp.status === 'success') {
        setMessages((prev) => [...prev, resp.body.questionMessage, resp.body.response])
      }
    })
  }

  const sortedMessages = useMemo(
    () => [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()),
    [messages]
  )

  return {
    messages: sortedMessages,
    messagesEndRef,
    messagesLoading,
    asking,
    handleAsk,
  }
}

export default useAiAssistanceMessagingCtrl
